import { useEffect, useState } from "react";

/**
 * Hook to detect if the current viewport is mobile size
 * @returns {boolean} true if viewport width is <= 768px
 */
export const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.innerWidth <= 768,
  );

  useEffect(() => {
    if (typeof window === "undefined") return;

    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  return isMobile;
};

/**
 * Hook to detect if the current viewport is tablet size
 * @returns {boolean} true if viewport width is between 769px and 1024px
 */
export const useIsTablet = () => {
  const [isTablet, setIsTablet] = useState(
    () =>
      typeof window !== "undefined" &&
      window.innerWidth > 768 &&
      window.innerWidth <= 1024,
  );

  useEffect(() => {
    if (typeof window === "undefined") return;

    const checkTablet = () => {
      setIsTablet(window.innerWidth > 768 && window.innerWidth <= 1024);
    };

    window.addEventListener("resize", checkTablet);
    return () => window.removeEventListener("resize", checkTablet);
  }, []);

  return isTablet;
};

/**
 * Hook to detect if the device is a touch-capable tablet (iPad, Android tablet, etc.)
 * Checks for touch capability, a coarse primary pointer, no hover, and viewport
 * wider than phone size (>768px). This avoids classifying normal laptops as
 * tablets just because they report touch support in the browser.
 * @returns {boolean} true if device is a touch tablet (not phone, not desktop)
 */
export const useIsTouchTablet = () => {
  const detectTouchTablet = () => {
    if (typeof window === "undefined") return false;

    const hasTouch = "ontouchstart" in window || navigator.maxTouchPoints > 0;
    const hasCoarsePointer = window.matchMedia("(pointer: coarse)").matches;
    const hasHover = window.matchMedia("(any-hover: hover)").matches;
    const userAgent = navigator.userAgent || "";
    const platform = navigator.platform || "";

    // iPadOS 13+ reports itself as MacIntel, so include maxTouchPoints heuristic.
    const isIPadOSDesktopUA =
      platform === "MacIntel" && navigator.maxTouchPoints > 1;
    const isIPad = /iPad/i.test(userAgent) || isIPadOSDesktopUA;
    const isAndroidTablet =
      /Android/i.test(userAgent) && !/Mobile/i.test(userAgent);
    const isKnownTabletUA = isIPad || isAndroidTablet;

    return (
      hasTouch &&
      hasCoarsePointer &&
      !hasHover &&
      window.innerWidth > 768 &&
      isKnownTabletUA
    );
  };

  const [isTouchTablet, setIsTouchTablet] = useState(() => {
    return detectTouchTablet();
  });

  useEffect(() => {
    if (typeof window === "undefined") return;

    const check = () => {
      setIsTouchTablet(detectTouchTablet());
    };

    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  return isTouchTablet;
};
